import { useMemo } from 'react'
import { altitudeProblem, type IrrigationFaultEventKind } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { isOpen } from '../sync/projectors.ts'
import type { MembershipRow, PersonRow } from './people.ts'

/** Water and weather as the device holds it (M14, ADR-102): what was irrigated, what went wrong, and the club's parameters for the advice. READ-ONLY records: nothing here is ever sent to a controller.
 *  M16-L1 adds what the club completes with no controller: a run's cycles and soak, the events on a fault (who saw it, who was given it, which job follows it) and the site's constraints. */
export interface RunRow {
  id: string; area_id: string; station_feature_id: string | null; kind: 'program' | 'manual' | 'test'; started_at: string; started_local_date: string
  duration_min: number | null; depth_mm: number | null; volume_m3: number | null; note: string | null; source: 'manual' | 'import'; entered_at: string
  actor_person_id?: string | null; corrects_run_id: string | null; correction_reason: string | null; superseded_by_id: string | null
  /** M16-L1: as read on the controller (a row pulled before the columns existed carries neither). */
  cycles?: number | null; soak_min?: number | null
}
export interface FaultRow {
  id: string; area_id: string | null; station_feature_id: string | null; kind: string; description: string; observed_at: string; observed_local_date: string
  resolved_at: string | null; resolution_note: string | null; actor_person_id?: string | null
}
export interface ProfileRow {
  id: string; scope: 'site' | 'area_type'; area_type_code: string | null; kc: number | null; root_zone_mm: number | null; precip_mm_h: number | null
  latitude: number | null; gdd_base_c: number | null; note: string | null; archived_at: string | null; revision: number
  /** M16-L1: the site's constraints (the site row only). */
  constraints?: string | null
  /** M24-M: the course's altitude in whole metres (the site row only; for the forecast's Penman-Monteith). A row pulled before the column existed carries no key: none. */
  altitude_m?: number | null
}
/** M16-L1: one thing that happened to a fault between people (ops.irrigation_fault_event, a ledger mirrored on personal devices only). */
export interface FaultEventRow {
  id: string; fault_id: string; kind: IrrigationFaultEventKind; person_id: string | null; job_id: string | null; note: string | null; at: string; actor_person_id?: string | null; sync_seq?: number
}
export const useRuns = () => useMerged<RunRow>('irrigation_run', 'ops.irrigation_run')
export const useFaults = () => useMerged<FaultRow>('irrigation_fault', 'ops.irrigation_fault')
export const useProfiles = () => useMerged<ProfileRow>('water_profile', 'ops.water_profile')
export const useFaultEvents = () => useMerged<FaultEventRow>('irrigation_fault_event', 'ops.irrigation_fault_event')

/** The records ready for a screen: runs that count (a corrected one is replaced by its correction), the open parameters. */
export function useWater() {
  const runs = useRuns(); const faults = useFaults(); const profiles = useProfiles()
  return useMemo(() => ({
    ready: !!runs && !!faults && !!profiles,
    allRuns: runs ?? [],
    runs: (runs ?? []).filter((r) => !r.superseded_by_id),
    faults: faults ?? [],
    profiles: (profiles ?? []).filter((p) => !p.archived_at),
    allProfiles: profiles ?? [],
  }), [runs, faults, profiles])
}
export type WaterRun = Pending<RunRow>
export type WaterFault = Pending<FaultRow>
export type WaterFaultEvent = Pending<FaultEventRow>

// ---- M16-L1: the pure rules of the screens (tested in water.test.ts) --------------------------------------------------------------------------------------

/** M24-M: the course's altitude, from the open site row (null when none, or not typed: the method then names it as missing, never a default). */
export const siteAltitude = (profiles: readonly ProfileRow[]): number | null => profiles.find((p) => p.scope === 'site' && !p.archived_at)?.altitude_m ?? null

/** M24-M: what stops the site card (latitude, base temperature, altitude) from being sent, the server's rules on the device: nothing typed, a value that cannot be read,
 *  or one out of its bounds (latitude -90..90, base temperature -10..30, altitude whole metres -100..3000: WATER_ALTITUDE_INVALID's twin, `altitudeProblem`). */
export type SiteFormValue = number | null | 'invalid'
export function siteFormProblem(v: { latitude: SiteFormValue; gddBase: SiteFormValue; altitude: SiteFormValue }): 'empty' | 'latitude' | 'gddBase' | 'altitude' | null {
  if (v.latitude === 'invalid' || (typeof v.latitude === 'number' && (v.latitude < -90 || v.latitude > 90))) return 'latitude'
  if (v.gddBase === 'invalid' || (typeof v.gddBase === 'number' && (v.gddBase < -10 || v.gddBase > 30))) return 'gddBase'
  if (v.altitude === 'invalid' || (typeof v.altitude === 'number' && altitudeProblem(v.altitude) !== null)) return 'altitude'
  if (v.latitude === null && v.gddBase === null && v.altitude === null) return 'empty'
  return null
}

/** The site's constraints, from the open site row (null when none, or none written). */
export const siteConstraints = (profiles: readonly ProfileRow[]): string | null => profiles.find((p) => p.scope === 'site' && !p.archived_at)?.constraints?.trim() || null

/** The events of one fault in the order they happened (the server's `at`, then its sequence: two events of one moment keep the server's order). */
export function faultEventsOf(events: readonly FaultEventRow[], faultId: string): FaultEventRow[] {
  return events.filter((e) => e.fault_id === faultId).sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : (a.sync_seq ?? 0) - (b.sync_seq ?? 0) || a.id.localeCompare(b.id)))
}
export interface FaultState {
  /** Who saw it (each person once, in order). */
  seenBy: Array<{ personId: string | null; at: string }>
  /** The current assignee (the latest assignment), with its moment; null while nobody has it. */
  assignee: { personId: string; at: string } | null
  /** The current repair job (the latest link); null until one follows. */
  job: { jobId: string; at: string } | null
}
/** What the fault's events say today: the latest assignment and the latest link are the current ones (the server's rule: an earlier one stays as history). */
export function faultState(events: readonly FaultEventRow[]): FaultState {
  const seen = new Map<string | null, string>()
  let assignee: FaultState['assignee'] = null, job: FaultState['job'] = null
  for (const e of events) {
    if (e.kind === 'acknowledged') { const who = e.actor_person_id ?? null; if (!seen.has(who)) seen.set(who, e.at) }
    else if (e.kind === 'assigned' && e.person_id) assignee = { personId: e.person_id, at: e.at }
    else if (e.kind === 'job_linked' && e.job_id) job = { jobId: e.job_id, at: e.at }
  }
  return { seenBy: [...seen].map(([personId, at]) => ({ personId, at })), assignee, job }
}

export interface RolePermissionRow { id: string; role_code: string; permission_code: string }
export interface AssigneeOption { id: string; name: string }
/** Who a fault may be given to: the human, active people of the club with an open membership at the site whose role holds job.execute (the server's rule,
 *  ASSIGNEE_INVALID otherwise), by name. */
export function assigneeOptions(persons: readonly PersonRow[], memberships: readonly MembershipRow[], rolePermissions: readonly RolePermissionRow[], siteId: string): AssigneeOption[] {
  const roles = new Set(rolePermissions.filter((rp) => rp.permission_code === 'job.execute').map((rp) => rp.role_code))
  const people = new Set(memberships.filter((m) => m.site_id === siteId && roles.has(m.role_code) && isOpen(m)).map((m) => m.person_id))
  return persons.filter((p) => p.kind === 'human' && p.employment_state === 'active' && people.has(p.id)).map((p) => ({ id: p.id, name: p.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
}

/** The cycles and the soak of a run as one short text ("3 cycles · pause 15 min"), or null when the run carries neither. */
export function runDetailsText(r: Pick<RunRow, 'cycles' | 'soak_min'>, t: (k: string, o?: Record<string, unknown>) => string, fmt: (n: number) => string): string | null {
  const parts: string[] = []
  if (r.cycles != null) parts.push(t('water.runs.cyclesText', { count: r.cycles }))
  if (r.soak_min != null) parts.push(t('water.runs.soakText', { min: fmt(r.soak_min) }))
  return parts.length ? parts.join(' · ') : null
}
